<template>
  <div class="mt-3" v-if="isOwner" id="userCashierContainer">
    <select-shop :loadingButton="loadingButtonUser" v-on:getData="getUserCashier"></select-shop>
    <CCard>
      <CCardBody>
        <!-- ===== Cashier View ===== -->
        <div v-if="currentView === 'cashier'" id="userCashierCashierView">
          <CRow id="userCashierHeaderRow">
            <CCol lg="12" md="12" class="text-center" id="userCashierHeaderCol">
            <div class="d-inline-flex align-items-start" id="userCashierHeaderTitle">
              <h2 class="font-weight-medium text-dark mb-0" id="userCashierTitle">
                {{ $t('cashierManagement') }}
              </h2>
              <HelpButton :isHeader="false" class="ml-2 mt-2" id="userCashierHelpButton"></HelpButton>
            </div>
              <h6 class="text-description mt-3" id="userCashierDescription">
                {{ $t('cashierPageDescription') }}
              </h6>
            </CCol>
          </CRow>
          <hr />

          <!-- Action Buttons -->
          <div class="d-flex justify-content-between align-items-center mb-4" id="userCashierActionRow">
              <button
                class="btn btn-success btn-sm"
                @click="openAddCashierModal()"
                id="userCashierAddButton"
                style="border-radius: 8px; padding: 6px 16px; font-weight: 500;"
              >
                <i class="fi fi-rr-plus mr-1"></i> {{ $t('addCashier') }} 
              </button>
              <div class="d-flex align-items-center">
                <div class="input-group input-group-sm mr-3" style="width: 240px;">
                  <div class="input-group-prepend">
                    <span class="input-group-text" style="background: #f8f9fa; border-right: 0;">
                      <i class="fi fi-rr-search" style="font-size: 12px; color: #999;"></i>
                    </span>
                  </div>
                  <input
                    type="text"
                    v-model="searchQuery"
                    @input="onSearchInput"
                    class="form-control"
                    :placeholder="$t('searchCashier') || 'ค้นหาพนักงาน...'"
                    style="border-left: 0;"
                  />
                </div>
              <button
                class="btn btn-outline-success btn-sm"
                @click="currentView = 'role'"
                id="userCashierGoToRoleButton"
                  style="border-radius: 8px; padding: 6px 16px; font-weight: 500;"
              >
                <i class="fi fi-rr-shield-check mr-1"></i> {{ $t('manageRole') }}
              </button>
              </div>
          </div>

          <!-- Summary Stats -->
          <div class="d-flex mb-3" style="gap: 12px;">
            <div class="uc-stat-card">
              <div class="uc-stat-num">{{ data.length }}</div>
              <div class="uc-stat-label">{{ $t('totalStaff') || 'พนักงานทั้งหมด' }}</div>
            </div>
            <div class="uc-stat-card uc-stat-card--admin">
              <div class="uc-stat-num">{{ adminCount }}</div>
              <div class="uc-stat-label">Admin</div>
            </div>
            <div class="uc-stat-card uc-stat-card--staff">
              <div class="uc-stat-num">{{ staffCount }}</div>
              <div class="uc-stat-label">{{ $t('cashier') || 'พนักงาน' }}</div>
            </div>
          </div>

          <!-- Cashier List (filtered) -->
          <ul class="list-group list-group-flush uc-list mb-3" id="userCashierList">
            <li
              class="list-group-item uc-list-item"
              v-for="(cashier, index) in data"
              :key="cashier.objectId"
              :id="`userCashierItem-${index}`"
            >
              <div class="d-flex align-items-center">
                <!-- Avatar with initials -->
                <div class="uc-avatar" :style="{ backgroundColor: getAvatarColor(index) }">
                  {{ getInitials(cashier.username) }}
                </div>
                <!-- Info -->
                <div class="flex-grow-1 ml-3">
                  <div class="d-flex align-items-center">
                    <span class="font-weight-medium text-dark" style="font-size: 15px;">{{ cashier.username }}</span>
                    <span v-if="cashier.localUser" class="ml-2" style="font-size: 11px; color: #aaa;">
                    {{ cashier.localUser.username }}
                    </span>
                  </div>
                  <div class="mt-1">
                    <span v-if="cashier.role === 'admin'" class="uc-role-badge uc-role-badge--admin">
                      <i class="fi fi-rr-user-lock" style="font-size: 10px;"></i> Admin
                    </span>
                    <span v-else-if="cashier.staffRole" class="uc-role-badge uc-role-badge--role">
                      <i class="fi fi-rr-shield-check" style="font-size: 10px;"></i> {{ cashier.staffRole.name }}
                    </span>
                    <span v-else class="uc-role-badge uc-role-badge--default">
                      <i class="fi fi-rr-user" style="font-size: 10px;"></i> {{ $t('cashier') }}
                    </span>
                  </div>
                </div>
                <!-- Action -->
                <button
                  @click="manage(cashier)"
                  class="btn btn-outline-dark btn-sm uc-manage-btn"
                  :id="`userCashierManageButton-${index}`"
                >
                    <i class="fi fi-rr-settings mr-1" style="font-size: 11px;"></i>
                    {{ $t('cashierManageButton') }}
                </button>
              </div>
            </li>
            <li v-if="data.length === 0" class="list-group-item text-center text-muted py-5" id="userCashierEmptyItem">
              <i class="fi fi-rr-user" style="font-size: 32px; color: #ddd;"></i>
              <div class="mt-2">
              {{ $t('noCashierFound') }}
              </div>
            </li>
          </ul>

          <pagination :meta_data="meta_data" v-on:next="goToPage" ></pagination>

          <!-- ==================== -->
          <!-- ADD CASHIER MODAL    -->
          <!-- ==================== -->
          <modal
            id="userCashierAddModal"
            :show.sync="addModal"
            :loading="!addLoading"
            :title="$t('addCashier')"
            centered
            color="success"
            @isSave="addCashier()"
          >
            <div class="uc-modal-desc">
              <i class="fi fi-rr-info" style="color: #41b883;"></i>
              <span>{{ $t('addCashierDescription') }}</span>
            </div>

            <CAlert color="success" v-if="addSuccessAlert !== ''" id="userCashierAddSuccessAlert">
              {{ addSuccessAlert }}
            </CAlert>
            <CAlert color="danger" v-if="addErrorAlert !== ''" id="userCashierAddErrorAlert">
              {{ addErrorAlert }}
            </CAlert>

            <form class="mt-3" id="userCashierAddForm">
              <div class="uc-form-group" id="userCashierAddNameRow">
                <label class="uc-form-label">
                  <i class="fi fi-rr-user mr-1"></i> {{ $t('cashierName') }}
                </label>
                  <input
                    type="text"
                    v-model="addForm.username"
                    class="form-control uc-form-input"
                    :placeholder="$t('cashierNamePlaceholder')"
                    id="userCashierAddNameInput"
                  />
              </div>

              <div class="uc-form-group" id="userCashierAddPinRow">
                <label class="uc-form-label">
                  <i class="fi fi-rr-key mr-1"></i> PIN
                </label>
                  <input
                    type="password"
                    v-model="addForm.password"
                    class="form-control uc-form-input"
                    maxlength="4"
                    :placeholder="$t('pinPlaceholder')"
                    id="userCashierAddPinInput"
                  />
                <small class="text-muted mt-1 d-block">{{ $t('pinHint') || 'รหัส PIN 4 หลัก สำหรับเข้าใช้งานเครื่อง POS' }}</small>
              </div>
              <div class="uc-form-group" id="userCashierAddUserTypeRow">
                <label class="uc-form-label">
                  <i class="fi fi-rr-briefcase mr-1"></i> {{ $t('userType') || 'User Type' }}
                </label>
                <select
                  v-model="addForm.userType"
                  class="form-control uc-form-input"
                  id="userCashierAddUserTypeSelect"
                >
                  <option v-for="opt in userTypeOptions" :key="opt.userType" :value="opt.userType">{{ opt.userType }}</option>
                </select>
                <small v-if="addForm.userType === 'EMPLOYEE'" class="text-warning mt-1 d-block">
                  <i class="fi fi-rr-exclamation mr-1" style="font-size: 11px;"></i>
                  {{ $t('employeeTypeHint') || 'EMPLOYEE สามารถเข้าออกงาน (Clock In/Out) ผ่านเครื่อง POS เท่านั้น ไม่สามารถเข้าใช้งานระบบได้' }}
                </small>
              </div>
            </form>
          </modal>

          <!-- ==================== -->
          <!-- MANAGE CASHIER MODAL -->
          <!-- ==================== -->
          <modal
            id="userCashierManageModal"
            :show.sync="popupModal"
            :loading="true"
            :title="$t('cashierManageButton')"
            :footer="false"
            centered
            size="lg"
            color="success"
          >

            <!-- Section: ข้อมูลพนักงาน + PIN -->
            <div class="uc-section" id="userCashierEditSection">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="uc-section-title mb-0">
                  <i class="fi fi-rr-user mr-2"></i>{{ $t('cashierInfo') }}   
                      <span v-if="currentCasheir.role !== 'admin'" class="text-center ml-2 " >
                        <CButton class=" btn-outline-danger "
                          style="border-radius: 8px;  font-size: 12px;"
                          @click="confirmDeleteModal = true"
                          id="userCashierDeleteButton">
                          <i class="fi fi-rr-trash " style="font-size: 12px;"></i> 
                        </CButton>
                      </span>
                </h6>
                <div>
                  <button v-if="editNameLoading === false" type="button" class="btn btn-success btn-sm" style="border-radius: 8px; padding: 4px 16px; font-size: 12px;" @click="saveCashierInfo()">
                    <i class="fi fi-rr-disk mr-1" style="font-size: 11px;"></i> {{ $t('save') }}
                  </button>
                  <button v-else type="button" class="btn btn-success btn-sm" style="border-radius: 8px; padding: 4px 16px; font-size: 12px;" disabled>
                    <CSpinner color="white" size="sm" />
                  </button>
                </div>
              </div>
              <CAlert color="success" v-if="editNameSuccess !== ''">{{ editNameSuccess }}</CAlert>
              <CAlert color="danger" v-if="editNameError !== ''">{{ editNameError }}</CAlert>
              <div class="d-flex align-items-center mb-3" style="gap: 10px;">
                <label class="mb-0 font-weight-normal text-nowrap" style="min-width: 80px;">{{ $t('cashierName') }}</label>
                <input type="text" v-model="editUsername" class="form-control form-control-sm" :placeholder="$t('cashierNamePlaceholder')" />
              </div>
              <div class="d-flex align-items-center mb-3" style="gap: 10px;">
                <label class="mb-0 font-weight-normal text-nowrap" style="min-width: 80px;">PIN</label>
                <input type="password" v-model="newPin" class="form-control form-control-sm" maxlength="4" :placeholder="$t('pinPlaceholder')" />
              </div>
              <div class="d-flex align-items-center mb-3" style="gap: 10px;">
                <label class="mb-0 font-weight-normal text-nowrap" style="min-width: 80px;">{{ $t('userType') || 'User Type' }}</label>
                <select v-model="editUserType" class="form-control form-control-sm">
                  <option v-for="opt in userTypeOptions" :key="opt.userType" :value="opt.userType">{{ opt.userType }}</option>
                </select>
              </div>
              <small v-if="editUserType === 'EMPLOYEE'" class="text-warning d-block mb-3">
                  <i class="fi fi-rr-exclamation mr-1" style="font-size: 11px;"></i>
                  {{ $t('employeeTypeHint') || 'EMPLOYEE สามารถเข้าออกงาน (Clock In/Out) ผ่านเครื่อง POS เท่านั้น ไม่สามารถเข้าใช้งานระบบได้' }}
                </small>
              <div v-if="currentCasheir.role !== 'admin'" class="d-flex justify-content-between align-items-center  py-2" style="border-top: 1px solid #f0f0f0; ">
                <div>
                  <span style="font-size: 14px; font-weight: 500; color: #333;">{{ $t('enabledCashier') || 'เปิดใช้งานพนักงาน' }}</span>
                  <div style="font-size: 11px; color: #999;">{{ $t('enabledCashierDesc') || 'ปิดเพื่อระงับการเข้าใช้งานชั่วคราว' }}</div>
                </div>
                <CSwitch color="success" :checked.sync="editEnabled" />
              </div>
              <!-- <div v-if="currentCasheir.role !== 'admin'" class="text-center pt-2" style="border-top: 1px solid #f0f0f0;">
                <button class="btn btn-outline-danger btn-sm"
                  style="border-radius: 8px; padding: 3px 14px; font-size: 12px;"
                  @click="confirmDeleteModal = true"
                  id="userCashierDeleteButton">
                  <i class="fi fi-rr-trash mr-1" style="font-size: 11px;"></i> {{ $t('deleteCashier') }}
                </button>
              </div> -->
            </div>



            <!-- Section 3: Reset Password (if has localUser) -->
            <template v-if="selfOrderEnabled">
            <div v-if="localUsername !== ''" class="uc-section" id="userCashierLoginSection">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="uc-section-title mb-0">
                  <i class="fi fi-rr-lock mr-2"></i>{{ $t('resetPassword') }}
                </h6>
                <button v-if="loadingButton === false" class="btn btn-outline-info btn-sm" @click="resetPassword()">{{ $t('resetPassword') }}</button>
                <button v-else class="btn btn-outline-info btn-sm" disabled><CSpinner color="info" size="sm" /> {{ $t('resetPassword') }}</button>
              </div>
              <small class="text-muted d-block mb-3">{{ $t('cashierCRUDDescription') }}</small>
              <CAlert color="success" v-if="successAlert !== ''">{{ successAlert }}</CAlert>
              <CAlert color="danger" v-if="errorAlert !== ''">{{ errorAlert }}</CAlert>
              <div class="form-group mb-2">
                <label class="font-weight-normal small">{{ $t('username') }}</label>
                <input type="text" :value="localUsername" class="form-control form-control-sm" disabled />
              </div>
              <div class="form-group mb-2">
                <label class="font-weight-normal small">{{ $t('oldPassword') }}</label>
                <input type="password" v-model="oldPassword" class="form-control form-control-sm" :placeholder="$t('oldPasswordPlaceholder')" />
              </div>
              <div class="form-group mb-2">
                <label class="font-weight-normal small">{{ $t('newPassword') }}</label>
                <input type="password" v-model="localpassword" class="form-control form-control-sm" :placeholder="$t('placeholderPassword')" />
              </div>
            </div>

            <!-- Section 4: สร้าง Login Account (if no localUser) -->
            <div v-else class="uc-section" id="userCashierCreateLoginSection">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="uc-section-title mb-0">
                  <i class="fi fi-rr-lock mr-2"></i>{{ $t('cashierUsernamePassword') }}
                </h6>
                <button v-if="loadingButton === false" class="btn btn-success btn-sm" @click="createCashierLogin()">{{ $t('save') }}</button>
                <button v-else class="btn btn-success btn-sm" disabled><CSpinner color="white" size="sm" /> {{ $t('save') }}</button>
              </div>
              <small class="text-muted d-block mb-3">{{ $t('cashierCRUDDescription') }}</small>
              <CAlert color="success" v-if="successAlert !== ''">{{ successAlert }}</CAlert>
              <CAlert color="danger" v-if="errorAlert !== ''">{{ errorAlert }}</CAlert>
              <div class="form-group mb-2">
                <label class="font-weight-normal small">{{ $t('username') }}</label>
                <input type="text" v-model="user" class="form-control form-control-sm" :placeholder="$t('username')" />
              </div>
              <div class="form-group mb-2">
                <label class="font-weight-normal small">{{ $t('password') }}</label>
                <input type="password" v-model="localpassword" class="form-control form-control-sm" :placeholder="$t('placeholderPassword')" />
              </div>
            </div>
            </template>


            <!-- Section: กำหนดสิทธิเข้าถึง + เลือก Role -->
            <div v-if="currentCasheir.userType !== 'admin' && currentCasheir.role !== 'admin'" class="uc-section" id="userCashierPermissionSection">
              <h6 class="uc-section-title">
                <i class="fi fi-rr-shield-check mr-2"></i>{{ $t('permissions') }}
              </h6>
              <small class="text-muted d-block mb-3">{{ $t('permissionDescription') }}</small>
              <div class="d-flex align-items-center mb-3">
                <label class="mb-0 mr-3 font-weight-normal text-nowrap">{{ $t('selectRole') }}</label>
                <select
                  class="form-control form-control-sm"
                  v-model="selectedRoleId"
                  @change="assignRole(currentCasheir, selectedRoleId)"
                >
                  <option value="">-- {{ $t('selectRole') }} --</option>
                  <option
                    v-for="role in staffRoles"
                    :key="role.objectId"
                    :value="role.objectId"
                  >
                    {{ role.name }}
                  </option>
                </select>
                <CSpinner v-if="assigningRole === currentCasheir.objectId" color="success" size="sm" class="ml-2" />
                <small v-if="roleAssignSuccess === currentCasheir.objectId" class="text-success ml-2">✓</small>
              </div>
              <div style="max-height: 250px; overflow-y: auto; margin-top: 12px;">
                <div
                  v-for="(perm, index) in cashierPermissionList"
                  :key="index"
                  class="d-flex justify-content-between align-items-center py-2" style="border-bottom: 1px solid #f0f0f0;"
                >
                  <div>
                    <span style="font-size: 13px; font-weight: 500; color: #333;">{{ perm.label }}</span>
                    <div style="font-size: 11px; color: #999; margin-top: 1px;">{{ perm.description }}</div>
                  </div>
                  <div class="d-flex align-items-center" style="gap: 6px;">
                    <span v-if="isOverrideFromRole(perm.key)"
                      class="uc-override-badge"
                      :title="getRoleValueLabel(perm.key)">
                      <i class="fi fi-rr-exclamation" style="font-size: 9px;"></i>
                      {{ $t('overrideFromRole') || 'ต่างจาก Role' }}
                    </span>
                    <small :class="getPermValue(perm.key) ? 'text-success' : 'text-danger'" style="white-space: nowrap;">
                      <i :class="getPermValue(perm.key) ? 'fi fi-rr-check-circle' : 'fi fi-rr-cross-circle'"></i>
                      {{ getPermValue(perm.key) ? $t('enable') : $t('disable') }}
                    </small>
                  </div>
                </div>
              </div>
              <small class="text-muted mt-2 d-block"><i class="fi fi-rr-info mr-1"></i>{{ $t('permissionReadOnlyHint') }}</small>
            </div>

          </modal>

          <!-- Confirm Delete Modal -->
          <modal
            id="userCashierDeleteModal"
            :show.sync="confirmDeleteModal"
            :loading="!deleteLoading"
            :title="$t('confirmDeleteCashier')"
            centered
            color="danger"
            @isSave="deleteCashier()"
          >
            <p class="text-muted" id="userCashierDeleteDescription">
              {{ $t('confirmDeleteCashierDescription') }} <b>{{ currentCasheir.username }}</b>
            </p>
          </modal>

        </div>

        <!-- ===== Role Management View ===== -->
        <StaffRole v-if="currentView === 'role'" @back="onBackFromRole()" @rolesUpdated="getStaffRoles()" id="userCashierStaffRoleView" />
        </CCardBody>
    </CCard>
  </div>
  <div v-else id="userCashierAccessDenied">
    <access-data></access-data>
  </div>
</template>

<script>
import local from '@/services/local'
import auth from '@/services/auth'
import { mapGetters } from 'vuex'
import permis from '@/util/permission'
import util from '@/util/util'
import HelpButton from '../../containers/HelpButton.vue'
import StaffRole from './StaffRole.vue'
import Modal from '../../containers/Modal.vue'
import Pagination from "@/containers/Pagination";
import { CButton } from '@coreui/vue'


export default {
  components: {
    HelpButton,
    StaffRole,
    Modal,
    Pagination
  },
  data() {
    return {
      loadingButtonUser: false,
      loadingButton: false,
      loading: '',
      searchQuery: '',
      popupModal: false,
      footer: '',
      data: [],
      allData: [],
      meta_data: {
        last_page: null,
        current_page: 1,
        prev_page_url: null,
        items: 0,
        limit: 20,
        itemCount: null,
      },
      resetRender: false,
      user: '',
      localpassword: '',
      oldPassword: '',
      currentCasheir: {},
      currentLocalUser: {},
      localUsername: '',
      successAlert: '',
      errorAlert: '',
      staffRoles: [],
      assigningRole: null,
      roleAssignSuccess: null,
      currentView: 'cashier',
      // PIN
      newPin: '',
      pinLoading: false,
      pinSuccessAlert: '',
      pinErrorAlert: '',
      // Edit Name
      editUsername: '',
      editEnabled: true,
      editNameLoading: false,
      editNameSuccess: '',
      editNameError: '',
      // Add Cashier
      addModal: false,
      addLoading: false,
      addSuccessAlert: '',
      addErrorAlert: '',
      addForm: {
        username: '',
        password: '',
        userType: 'POS',
      },
      // Delete
      confirmDeleteModal: false,
      deleteLoading: false,
      selectedRoleId: '',
      userTypeOptions: [
        { userType: 'POS' },
        { userType: 'EMPLOYEE' },
      ],
      editUserType: 'POS',
    }
  },
  created() {
    if (this.shopObjectId) {
      this.getUserCashier()
      this.getStaffRoles()
    }
  },
  mounted() {
    this.$store.dispatch('getUser')
  },
  watch: {
    shopObjectId(val) {
      if (val) {
        this.getUserCashier()
        this.getStaffRoles()
      }
    },
    confirmDeleteModal(val) {
      if (val) {
        this.$nextTick(() => {
          const overlay = document.createElement('div')
          overlay.id = 'uc-delete-overlay'
          overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:1055;'
          document.body.appendChild(overlay)
        })
      } else {
        const overlay = document.getElementById('uc-delete-overlay')
        if (overlay) overlay.remove()
      }
    },
  },
  computed: {
    ...mapGetters(['shops', 'users', 'date', 'isOwner']),

    adminCount() {
      return this.data.filter(c => c.role === 'admin').length;
    },
    staffCount() {
      return this.data.filter(c => c.role !== 'admin').length;
    },
    isPermission() {
      return permis.findPermissionRead('setting', this.$route.path)
    },
    uid() {
      return `${localStorage.getItem('shopsUid')}`
    },
    shopObjectId: {
      get() {
        return this.$store.getters.shopObjectId
      },
      set(newValue) {
        return this.$store.dispatch('setShop', newValue)
      },
    },
    cashierPermissionList() {
      return [
        { key: 'productManagementAccess', label: this.$t('permProductManagement'), description: this.$t('permProductManagementDesc') },
        { key: 'billManagementAccess', label: this.$t('permBillManagement'), description: this.$t('permBillManagementDesc') },
        { key: 'cancelBillAccess', label: this.$t('permCancelBill'), description: this.$t('permCancelBillDesc') },
        { key: 'cancelOrderAccess', label: this.$t('permCancelOrder'), description: this.$t('permCancelOrderDesc') },
        { key: 'inventoryAccess', label: this.$t('permInventory'), description: this.$t('permInventoryDesc') },
        { key: 'reportAccess', label: this.$t('permReport'), description: this.$t('permReportDesc') },
        { key: 'settingAccess', label: this.$t('permSetting'), description: this.$t('permSettingDesc') },
        { key: 'shopSettingsAccess', label: this.$t('permShopSettings'), description: this.$t('permShopSettingsDesc') },
        { key: 'customerAccess', label: this.$t('permCustomer'), description: this.$t('permCustomerDesc') },
        { key: 'drawerOpenAccess', label: this.$t('permDrawerOpen'), description: this.$t('permDrawerOpenDesc') },
        { key: 'userCashierAccess', label: this.$t('permUserCashier'), description: this.$t('permUserCashierDesc') },
        { key: 'netProfitAccess', label: this.$t('permNetProfit'), description: this.$t('permNetProfitDesc') },
        { key: 'drawerCashDetailAccess', label: this.$t('permDrawerCashDetail'), description: this.$t('permDrawerCashDetailDesc') },
        { key: 'stockInCreateAccess', label: this.$t('permStockInCreate'), description: this.$t('permStockInCreateDesc') },
        { key: 'stockInEditAccess', label: this.$t('permStockInEdit'), description: this.$t('permStockInEditDesc') },
        { key: 'stockOutCreateAccess', label: this.$t('permStockOutCreate'), description: this.$t('permStockOutCreateDesc') },
        { key: 'stockOutEditAccess', label: this.$t('permStockOutEdit'), description: this.$t('permStockOutEditDesc') },
        { key: 'adjustStockAccess', label: this.$t('permAdjustStock'), description: this.$t('permAdjustStockDesc') },
      ]
    },
    selectedRoleData() {
      if (!this.selectedRoleId) return null
      return this.staffRoles.find(
        (r) => r.objectId === this.selectedRoleId
      ) || null
    },
    currentShop() {
      return this.shops.find(s => s.objectId === this.shopObjectId) || {};
    },
    selfOrderEnabled() {
      return this.currentShop?.delivery?.isSelfOrderEnabled || false;
    },
  },
  methods: {
    getInitials(name) {
      if (!name) return '?';
      const parts = name.split(/[\s.]+/);
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return name.substring(0, 2).toUpperCase();
    },
    getAvatarColor(index) {
      const colors = ['#41b883', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];
      return colors[index % colors.length];
    },
    isOverrideFromRole(key) {
      const role = this.selectedRoleData;
      if (!role) return false;
      // Normalize: undefined/null/false → false, true → true
      const roleValue = !!role[key];
      const userValue = !!this.getPermValue(key);
      return roleValue !== userValue;
    },
    getPermValue(key) {
      // ถ้า user มี field นี้อยู่ → ใช้ค่าจาก user
      if (key in this.currentCasheir) {
        return this.currentCasheir[key];
      }
      // ถ้า user ไม่มี field นี้ (key ใหม่) → fallback ไปดูค่าจาก role
      const role = this.selectedRoleData;
      if (role && key in role) {
        return role[key];
      }
      return false;
    },
    getRoleValueLabel(key) {
      const role = this.selectedRoleData;
      if (!role) return '';
      return `Role: ${role[key] ? this.$t('enable') : this.$t('disable')}`;
    },
    // ==================== SAVE: Name + PIN ====================
    saveCashierInfo() {
      if (!this.editUsername || this.editUsername.trim() === '') {
        this.editNameError = this.$t('cashierNameRequired')
        return
      }
      // PIN is optional — only validate if user typed something
      if (this.newPin && !/^\d{4}$/.test(this.newPin)) {
        this.editNameError = this.$t('pinInvalid')
        return
      }

      this.editNameLoading = true
      this.editNameError = ''
      this.editNameSuccess = ''

      const headers = { shopobjectid: this.shopObjectId }
      const data = {
        objectId: this.currentCasheir.objectId,
        name: this.editUsername.trim(),
        enabled: this.editEnabled,
        userType: this.editUserType,
      }
      // Only send pin if user entered a new one
      if (this.newPin && this.newPin.trim() !== '') {
        data.pin = this.newPin.trim()
      }

      local
        .patch('/api/v3.0/staffrole/cashier/update', data, { headers })
        .then((res) => {
          if (res.data.success !== false) {
            this.editNameSuccess = this.$t('saveSuccess')
            this.editNameError = ''
            this.newPin = ''
            this.getUserCashier()
          } else {
            this.editNameSuccess = ''
            this.editNameError = (res.data.error?.message) || this.$t('errorOccurred')
          }
          this.editNameLoading = false
        })
        .catch((err) => {
          console.error('Save cashier info error:', err)
          this.editNameLoading = false
          this.editNameError = this.$t('connectionError')
        })
    },

    // ==================== READ ====================
    onSearchInput() {
      if (this._searchTimer) clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.meta_data.current_page = 1;
        this.getUserCashier(1);
      }, 400);
    },
    getUserCashier(page = 1) {
      const uid = this.uid
      const shopObjectId = this.shopObjectId
      const params = {
        searchVal : this.searchQuery,
        shopObjectId: shopObjectId,
        page: page,
        limit: 50,
      }
      this.loadingButtonUser = false

      local
        .get('/api/v1.0/' + uid + '/cashier/getall/' + shopObjectId, { params })
        .then((res) => {

          this.data = res.data.data || []

          if (res.data.paginate.items == undefined) {
            this.meta_data.items = 0
          } else {
            this.meta_data.items = res.data.paginate.items
          }
          this.meta_data.last_page = res.data.paginate.pageCount
          this.meta_data.current_page = res.data.paginate.currentPage
          this.meta_data.itemCount = res.data.paginate.itemCount
          this.meta_data.limit = res.data.paginate.perPage
          this.loadingButtonUser = true
          // this.paginateData()
        })
    },

    goToPage(page = 1) {
      this.getUserCashier(page)
    },

    // ==================== CREATE ====================
    openAddCashierModal() {
      this.addForm = { username: '', password: '', userType: 'POS' }
      this.addSuccessAlert = ''
      this.addErrorAlert = ''
      this.addModal = true
    },
    async addCashier() {
      if (!this.addForm.username || this.addForm.username.trim() === '') {
        this.addErrorAlert = this.$t('cashierNameRequired')
        return
      }
      if (!this.addForm.password || !/^\d{4}$/.test(this.addForm.password)) {
        this.addErrorAlert = this.$t('pinInvalid')
        return
      }

      this.addLoading = true
      this.addErrorAlert = ''
      this.addSuccessAlert = ''

      try {
        const headers = {
          shopobjectid: this.shopObjectId,
        }

        const payload = {
          name: this.addForm.username.trim(),
          pin: this.addForm.password,
          userType: this.addForm.userType,
        }

        const saveRes = await local.post(
          '/api/v3.0/staffrole/cashier/create',
          payload,
          { headers }
        )

        if (saveRes.data.success !== false) {
          this.addSuccessAlert = this.$t('addCashierSuccess')
          this.addForm = { username: '', password: '', userType: 'POS' }
          this.getUserCashier()
          this.addModal = false
        } else {
          this.addErrorAlert =
            (saveRes.data.error?.message) ||
            this.$t('addCashierFail')
        }
      } catch (e) {
        this.addErrorAlert = this.$t('addCashierFail')
      }

      this.addLoading = false
    },

    // ==================== MANAGE ====================
    manage(cashier) {
      this.popupModal = true
      this.successAlert = ''
      this.errorAlert = ''
      this.pinSuccessAlert = ''
      this.pinErrorAlert = ''
      this.editNameSuccess = ''
      this.editNameError = ''
      this.newPin = ''
      this.user = ''
      this.localpassword=''
      this.oldPassword=''

      this.currentCasheir = cashier
      this.currentLocalUser = cashier.localUser
      this.editUsername = cashier.username || ''
      this.editUserType = cashier.userType || 'POS'
      this.editEnabled = cashier.enabled !== false
      this.selectedRoleId = this.getCashierRoleId(cashier)

      if (this.currentLocalUser != undefined) {
        this.localUsername = this.currentLocalUser.username
      } else {
        this.localUsername = ''
      }
    },

    // ==================== UPDATE: Name ====================
    updateCashierName() {
      if (!this.editUsername || this.editUsername.trim() === '') {
        this.editNameError = this.$t('cashierNameRequired')
        return
      }

      this.editNameLoading = true
      this.editNameError = ''
      this.editNameSuccess = ''

      const headers = { shopobjectid: this.shopObjectId }
      const data = {
        objectId: this.currentCasheir.objectId,
        name: this.editUsername.trim(),
      }

      local
        .patch('/api/v3.0/staffrole/cashier/update', data, { headers })
        .then((res) => {
          if (res.data.error && res.data.error.code === 0) {
            this.editNameSuccess = this.$t('updateNameSuccess')
            this.editNameError = ''
            this.getUserCashier()
          } else {
            this.editNameSuccess = ''
            this.editNameError =
              (res.data.error && res.data.error.message) ||
              this.$t('updateNameFail')
          }
          this.editNameLoading = false
        })
        .catch(() => {
          this.editNameError = this.$t('updateNameFail')
          this.editNameLoading = false
        })
    },

    // ==================== UPDATE: PIN ====================
    updatePin() {
      if (!this.newPin || this.newPin.trim() === '') {
        this.pinErrorAlert = this.$t('pinRequired')
        return
      }
      if (!/^\d{4}$/.test(this.newPin)) {
        this.pinErrorAlert = this.$t('pinInvalid')
        return
      }

      this.pinLoading = true
      this.pinErrorAlert = ''
      this.pinSuccessAlert = ''

      const headers = { shopobjectid: this.shopObjectId }
      const data = {
        objectId: this.currentCasheir.objectId,
        pin: this.newPin.trim(),
      }

      local
        .patch('/api/v3.0/staffrole/cashier/update', data, { headers })
        .then((res) => {
          if (res.data.success !== false) {
            this.pinSuccessAlert = this.$t('pinUpdateSuccess')
            this.pinErrorAlert = ''
            this.newPin = ''
          } else {
            this.pinSuccessAlert = ''
            this.pinErrorAlert =
              (res.data.error && res.data.error.message) ||
              this.$t('pinUpdateFail')
          }
          this.pinLoading = false
        })
        .catch(() => {
          this.pinErrorAlert = this.$t('pinUpdateFail')
          this.pinLoading = false
        })
    },

    // ==================== CREATE LOGIN ====================
    createCashierLogin() {
      this.loadingButton = true
      let cashier = this.currentCasheir

      let data = {
        user: this.user,
        password: this.localpassword,
        shopObjectId:this.shopObjectId,
        userCashierObjectId: cashier.objectId,
        userCashier: {
          id: cashier.id,
          objectId: cashier.objectId,
          username: this.user,
          name: cashier.username,
        },
      }

      auth
        .post('/auth/v1.0/' + this.uid + '/localuser/create', data)
        .then((res) => {
          if (res.data.error.code === 0) {
            let response = res.data.data
            this.updateCashierLocalUser(response)
            this.errorAlert = ''
            this.successAlert = this.$t('saveSuccess')
          } else {
            this.successAlert = ''
            this.errorAlert = res.data.error.message
          }
          this.loadingButton = false
        })
    },
    updateCashierLocalUser(responseLocalUser) {
      let data = {
        shopObjectId: this.shopObjectId,
        objectId: responseLocalUser.userCashierObjectId,
        localUser: {
          id: responseLocalUser.id,
          objectId: responseLocalUser.objectId,
          username: responseLocalUser.user,
        },
      }

      local
        .post('/api/v1.0/' + this.uid + '/cashier/update', data)
        .then(() => {
          this.getUserCashier()
        })
    },

    // ==================== RESET PASSWORD=[REDACTED_PASSWORD]
    resetPassword() {
      this.loadingButton = true

      let data = {
        user: this.localUsername,
        password: this.localpassword,
        oldPassword: this.oldPassword,
      }

      auth
        .post('/auth/v1.0/' + this.uid + '/localuser/resetpassword', data)
        .then((res) => {
          if (res.data.error.code === 0) {
            this.errorAlert = ''
            this.successAlert = this.$t('saveSuccess')
          } else {
            this.successAlert = ''
            this.errorAlert = res.data.error.message
          }
          this.loadingButton = false
        })
    },

    // ==================== DELETE ====================
    deleteCashier() {
      this.deleteLoading = true

      const headers = {
        shopobjectid: this.shopObjectId,
      }

      const data = {
        objectId: this.currentCasheir.objectId,
      }

      local
        .delete('/api/v3.0/staffrole/cashier/delete', { headers, data })
        .then((res) => {
          if (res.data.success !== false) {
            this.confirmDeleteModal = false
            this.popupModal = false
            this.getUserCashier()
          }
          this.deleteLoading = false
        })
        .catch((err) => {
          console.error('Delete cashier error:', err)
          this.deleteLoading = false
        })
    },

    // ==================== STAFF ROLES ====================
    onBackFromRole() {
      this.currentView = 'cashier'
      this.getStaffRoles()
      this.getUserCashier()
    },
    getStaffRoles() {
      const headers = {
        shopobjectid: this.shopObjectId,
      }

      local
        .get('/api/v3.0/staffrole', { headers })
        .then((res) => {
          if (res.data.error && res.data.error.code === 0) {
            this.staffRoles = res.data.data || []
          } else {
            this.staffRoles = res.data.data || res.data || []
          }
            if (res.data.paginate.items == undefined) {
            this.meta_data.items = 0
          } else {
            this.meta_data.items = res.data.paginate.items
          }
          this.meta_data.last_page = res.data.paginate.pageCount
          this.meta_data.current_page = res.data.paginate.currentPage
          this.meta_data.itemCount = res.data.paginate.itemCount
          this.meta_data.limit = res.data.paginate.perPage
        })
        .catch(() => {
          this.staffRoles = []
        })
    },
    getCashierRoleId(cashier) {
      if (cashier.staffRoleObjectId) {
        return cashier.staffRoleObjectId
      }
      if (cashier.staffRole && cashier.staffRole.objectId) {
        return cashier.staffRole.objectId
      }
      return ''
    },
    assignRole(cashier, roleObjectId) {
      if (!roleObjectId) return

      this.assigningRole = cashier.objectId
      this.roleAssignSuccess = null

      const headers = {
        shopobjectid: this.shopObjectId,
        'content-type': 'application/json',
      }

      const payload = {
        objectId: roleObjectId,
        cashierObjectId: cashier.objectId,
      }

      local
        .post('/api/v3.0/staffrole/cashier', payload, { headers })
        .then((res) => {
          this.assigningRole = null
          if (res.data.error && res.data.error.code === 0) {
            this.roleAssignSuccess = cashier.objectId
            setTimeout(() => {
              this.roleAssignSuccess = null
            }, 2000)
            this.getStaffRoles()
            // Refresh cashier list and update currentCasheir
            const uid = this.uid
            const shopObjectId = this.shopObjectId
            local
              .get('/api/v1.0/' + uid + '/cashier/getall/' + shopObjectId)
              .then((res) => {
                this.data = res.data.data
                // Update currentCasheir with fresh data
                const updated = this.data.find((c) => c.objectId === cashier.objectId)
                if (updated) {
                  this.currentCasheir = updated
                }
              })
          }
        })
        .catch(() => {
          this.assigningRole = null
        })
    },
  },
}
</script>

<style scoped>
/* Summary Stats */
.uc-stat-card {
  flex: 1;
  background: #f8f9fa;
  border-radius: 10px;
  padding: 12px 16px;
  text-align: center;
  border: 1px solid #e9ecef;
}
.uc-stat-card--admin { background: #fff3e0; border-color: #ffe0b2; }
.uc-stat-card--staff { background: #e8f5e9; border-color: #c8e6c9; }
.uc-stat-num { font-size: 22px; font-weight: 700; color: #333; }
.uc-stat-label { font-size: 12px; color: #888; margin-top: 2px; }

/* Cashier List */
.uc-list { border-radius: 10px; overflow: hidden; border: 1px solid #e9ecef; }
.uc-list-item {
  padding: 14px 16px;
  transition: background-color 0.15s;
  border-bottom: 1px solid #f0f0f0;
}
.uc-list-item:last-child { border-bottom: none; }
.uc-list-item:hover { background-color: #fafffe; }

/* Avatar */
.uc-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
}

/* Role Badges */
.uc-role-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 500;
}
.uc-role-badge--admin { background: #fff3e0; color: #e65100; }
.uc-role-badge--role { background: #e8f5e9; color: #2e7d32; }
.uc-role-badge--default { background: #f5f5f5; color: #757575; }

/* Manage Button */
.uc-manage-btn {
  border-radius: 8px;
  padding: 5px 14px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.15s;
}
.uc-manage-btn:hover {
  background: #333;
  color: white;
}

/* Modal Sections */
.uc-section {
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 10px;
  padding: 16px 18px;
  margin-bottom: 14px;
}
.uc-section-title {
  font-weight: 600;
  color: #333;
  font-size: 14px;
  margin-bottom: 12px;
}

/* Modal Form (Add) */
.uc-modal-desc {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f0faf5;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #555;
}
.uc-form-group { margin-bottom: 16px; }
.uc-form-label { font-weight: 500; font-size: 14px; color: #444; margin-bottom: 6px; display: block; }
.uc-form-input { border-radius: 8px; }

/* Modal scroll fix */
#userCashierManageModal  .modal-body {
  max-height: calc(100vh - 160px);
  overflow-y: auto;
  padding: 16px 20px;
  scrollbar-width: thin;
}


/* Override Badge */
.uc-override-badge {

  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 10px;
  background: #fff3e0;
  color: #e65100;
  font-weight: 500;
  white-space: nowrap;
  cursor: help;
}
</style>
<style>
#userCashierDeleteModal .modal {
  z-index: 1060 !important;
}
</style>
